import React, { useEffect, useMemo } from 'react';
import './ConfirmationModal.css';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  text: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  text,
  confirmButtonText = "Yes, do it!",
  cancelButtonText = "Cancel",
}) => {
  const maskId = useMemo(() => `confirmation-mask-${Math.random().toString(36).substring(2, 11)}`, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <div className="confirmation-modal-overlay" onClick={handleBackdropClick}>
      <div className="confirmation-modal-container">
        <button className="confirmation-modal-close" onClick={onClose}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        <div className="confirmation-modal-icon">
          <svg width="80" height="77" viewBox="0 0 80 77" fill="none" xmlns="http://www.w3.org/2000/svg">
            <mask id={maskId} fill="white">
              <path d="M0 38.5C0 17.237 17.237 0 38.5 0H41.5C62.763 0 80 17.237 80 38.5C80 59.763 62.763 77 41.5 77H38.5C17.237 77 0 59.763 0 38.5Z"/>
            </mask>
            <path d="M38.5 0V4H41.5V0V-4H38.5V0ZM41.5 77V73H38.5V77V81H41.5V77ZM38.5 77V73C19.4462 73 4 57.5538 4 38.5H0H-4C-4 61.9721 15.0279 81 38.5 81V77ZM80 38.5H76C76 57.5538 60.5538 73 41.5 73V77V81C64.9721 81 84 61.9721 84 38.5H80ZM41.5 0V4C60.5538 4 76 19.4462 76 38.5H80H84C84 15.0279 64.9721 -4 41.5 -4V0ZM38.5 0V-4C15.0279 -4 -4 15.0279 -4 38.5H0H4C4 19.4462 19.4462 4 38.5 4V0Z" fill="#FF8904" mask={`url(#${maskId})`}/>
            <path d="M40 58.5C51.0457 58.5 60 49.5457 60 38.5C60 27.4543 51.0457 18.5 40 18.5C28.9543 18.5 20 27.4543 20 38.5C20 49.5457 28.9543 58.5 40 58.5Z" stroke="#FF8904" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M40 30.5V38.5" stroke="#FF8904" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M40 46.5H40.02" stroke="#FF8904" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>

        <h2 className="confirmation-modal-title">{title}</h2>
        
        <p className="confirmation-modal-text">{text}</p>

        <div className="confirmation-modal-buttons">
          <button className="confirmation-modal-button-cancel" onClick={onClose}>
            {cancelButtonText}
          </button>
          <button className="confirmation-modal-button-confirm" onClick={handleConfirm}>
            {confirmButtonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;

